import {
  Component,
  Input,
  ViewEncapsulation,
  AfterViewInit,
  ChangeDetectionStrategy,
} from "@angular/core";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import Quill from "quill";
import { Mention, MentionBlot } from "quill-mention";
import { SHARED_IMPORTS } from "src/app/shared/standalone-imports";
Quill.register({ "blots/mention": MentionBlot, "modules/mention": Mention });

@Component({
  selector: "app-mention-textarea",
  templateUrl: "./mention-textarea.component.html",
  styleUrls: ["./mention-textarea.component.scss"],
  encapsulation: ViewEncapsulation.None,
  imports: SHARED_IMPORTS,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MentionTextareaComponent implements AfterViewInit {
  static MENTION_COLOR: string = "#65b3d1";
  static RECORD_COLOR: string = "#ffa305";
  static MENTION_OWN_COLOR: string = "#ea4879";

  @Input() mentionTargetUsers: { [key: string]: string } = {};
  @Input() mentionTargetRecords: { [key: string]: string } = {};
  @Input() innerId: string = "";
  @Input() innerClass: string = "";
  @Input() placeholder: string = "";
  @Input() innerStyle: string = "";

  _quill: Quill;
  _isFirstInsert: boolean = true;

  constructor() {}

  ngAfterViewInit() {
    setTimeout(() => {
      try {
        this._quill = new Quill(`#${this.innerId}`, {
          // debug: 'info',
          theme: "snow",
          placeholder: `${this.placeholder}`,
          modules: {
            toolbar: false,
            mention: {
              allowedChars: /^.*$/,
              mentionDenotationChars: ["@", "#"],
              source: (searchTerm, renderList, mentionChar) => {
                this._suggestTarget(searchTerm, renderList, mentionChar);
              },
              defaultMenuOrientation: "bottom",
              dataAttributes: ["id", "value"],
            },
          },
        });
      } catch (e) {
        console.error(
          `[${MentionTextareaComponent.name}] catch error in create quill / innerId=${this.innerId}, error=${e}`,
        );
      }
    }, 1);
  }

  _suggestTarget(searchTerm: string, renderList: any, mentionChar: string) {
    let targetValues = [];
    if (mentionChar === "@") {
      targetValues = Object.keys(this.mentionTargetUsers).map((key) => ({
        id: key,
        value: this.mentionTargetUsers[key],
      }));
    } else if (mentionChar === "#") {
      targetValues = Object.keys(this.mentionTargetRecords).map((key) => ({
        id: key,
        value: this.mentionTargetRecords[key],
      }));
    }

    if (searchTerm.length === 0) {
      renderList(targetValues, searchTerm);
    } else {
      const matches = [];
      for (let i = 0; i < targetValues.length; i++) {
        // monitorChar #のときに大文字小文字を区別しないようにする
        if (
          mentionChar === "#" &&
          targetValues[i].value.toLowerCase().includes(searchTerm.toLowerCase())
        ) {
          matches.push(targetValues[i]);
        } else if (
          mentionChar !== "#" &&
          targetValues[i].value.includes(searchTerm)
        ) {
          matches.push(targetValues[i]);
        }
      }
      renderList(matches, searchTerm);
    }
  }

  public isValid() {
    const inputedContent = this._quill.root.textContent;
    return inputedContent.trim().length > 0;
  }

  public confirmInput() {
    const currentUserMentions: { [key: string]: string } = {};
    let inputedContent = this._quill.root.innerHTML;
    inputedContent = inputedContent
      // .replace(/<br\s*\/?>/g, '\n')
      .replace(/<br\s*\/?>/g, "")
      .replace(/<\/p>/g, "\n")
      // .replace(/<\/p>/g, '')
      .replace(/<p>/g, "");

    this._quill.root.innerHTML = inputedContent;

    let textContent = this._quill.root.textContent;

    const mentionTargetNames = [
      ...new Set(Object.values(this.mentionTargetUsers)),
    ];
    mentionTargetNames.forEach((name) => {
      const mentionPattern = new RegExp(
        `@${name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}`,
        "g",
      );
      if (mentionPattern.test(textContent)) {
        const id = Object.keys(this.mentionTargetUsers).find(
          (key) => this.mentionTargetUsers[key] === name,
        );
        if (id) {
          currentUserMentions[id] = name;
          textContent = textContent.replace(
            mentionPattern,
            (match) => `[[[@${id}//${match.slice(1)}]]]`,
          );
        }
      }
    });

    const recordTargetNames = [
      ...new Set(Object.values(this.mentionTargetRecords)),
    ];
    recordTargetNames.forEach((name) => {
      const recordPattern = new RegExp(
        `#${name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}`,
        "g",
      );
      if (recordPattern.test(textContent)) {
        const id = Object.keys(this.mentionTargetRecords).find(
          (key) => this.mentionTargetRecords[key] === name,
        );

        if (id) {
          textContent = textContent.replace(
            recordPattern,
            (match) => `[[[#${id}//${match.slice(1)}]]]`,
          );
        }
      }
    });

    return {
      userMentions: currentUserMentions,
      content: textContent,
    };
  }

  public static genMentionPlainText(contentWithTags: string): string {
    const userPattern = /\[\[\[@([^\s/]+)\/\/([^\]]+?)]]]/g;
    let converted = contentWithTags.replace(userPattern, (match, id, name) => {
      return `@${name}`;
    });

    const recordPattern = /\[\[\[#([^\s/]+)\/\/([^\]]+?)]]]/g;
    converted = converted.replace(recordPattern, (match, id, name) => {
      return `#${name}`;
    });

    const textArea = document.createElement("textarea");
    textArea.innerHTML = converted;
    converted = textArea.value;

    return converted;
  }

  public static genMentionHtml(
    sanitizer: DomSanitizer,
    contentWithTags: string,
    ownUserId: string,
    dialogId: string,
    responseIndex: number,
  ): SafeHtml {
    if (!contentWithTags.includes("[[[")) {
      return contentWithTags;
    }

    const userPattern = /\[\[\[@([^\s/]+)\/\/([^\]]+?)]]]/g;

    let converted = contentWithTags.replace(userPattern, (match, id, name) => {
      const mentionClass = id === ownUserId ? "mention mention-own user" : "mention user";
      return `<span class="${mentionClass}" data-user-id="${id}">@${name}</span>`;
    });

    const recordPattern = /\[\[\[#([^\s/]+)\/\/([^\]]+?)]]]/g;
    converted = converted.replace(recordPattern, (match, id, name) => {
      return `<a href="#" class="mention record" data-record-id="${id}" data-dialog-id="${dialogId}" data-response-index="${responseIndex}">#${name}</a>`;
    });

    return sanitizer.bypassSecurityTrustHtml(converted);
  }

  public clearInput() {
    this._quill.setText("");
  }

  public setInput(newInput: string) {
    this._quill.setText(newInput);
  }

  public insertItem(denotationChar: string, id: string, value: string) {
    if (!this._quill) {
      console.warn(
        `[${MentionTextareaComponent.name}] Quill is not initialized yet.`,
      );
      return;
    }

    if (this._isFirstInsert == true) {
      // @ts-ignore
      this._quill.getModule("mention").openMenu(denotationChar);
      this._isFirstInsert = false;
    }

    this._quill
      .getModule("mention")
      // @ts-ignore
      .insertItem(
        { denotationChar: denotationChar, id: id, value: value },
        true,
      );
  }
}
